import React, { useEffect, useState, useMemo } from "react";
import { FaMobileAlt } from "react-icons/fa";
import { enqueueSnackbar } from "notistack";
import CardHeader from "../../../components/Card/CardHeader";
import Loader from "../../../components/Loader/Loader";
import Modal from "../../../components/Modal/Modal";
import Table from "../../../components/Table/Table";
import Input from "../../../components/Input/Input";
import {
  getParentAppFeatures,
  createParentAppFeature,
  updateParentAppFeature,
  updateOrgFeature,
} from "../../../services/AppManagement/ParentAppFeatureService";
import { tableConstants } from "./ParentAppFeatureTableConstant";
import ParentAppFeatureForm from "./ParentAppFeatureForm";
import "./ParentAppFeature.css";
import Button from "../../../components/Button/Button";

const EMPTY_FORM = {
  key: "",
  name: "",
  description: "",
  is_enabled_by_default: false,
};

const ParentAppFeatureList = () => {
  const [data, setData] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [togglingKeys, setTogglingKeys] = useState(new Set());
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editKey, setEditKey] = useState(null);

  const organizationId = Number(localStorage.getItem("organization_id"));
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");

  /* ─── Fetch helpers ─── */

  const fetchFeatures = async () => {
    try {
      setIsLoader(true);
      const response = await getParentAppFeatures();
      if (response.status === 200) {
        setData(response.data?.data || response.data || []);
      }
    } catch (error) {
      console.error("API Error:", error);
      enqueueSnackbar("Failed to fetch features", { variant: "error" });
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Search (client-side) ─── */

  const filteredData = useMemo(() => {
    if (!search.trim()) return data;
    const lower = search.toLowerCase();
    return data.filter(
      (item) =>
        item.key?.toLowerCase().includes(lower) ||
        item.name?.toLowerCase().includes(lower)
    );
  }, [data, search]);

  /* ─── Optimistic toggle ─── */

  const handleToggle = async (rowData) => {
    if (!organizationId) {
      enqueueSnackbar("No organization selected. Please select one from the header.", { variant: "warning" });
      return;
    }

    const featureKey = rowData.key;
    const currentIsEnabled =
      rowData.is_enabled !== undefined && rowData.is_enabled !== null
        ? rowData.is_enabled
        : rowData.is_enabled_by_default;
    const newIsEnabled = !currentIsEnabled;

    // Optimistic update
    setData((prev) =>
      prev.map((item) =>
        item.key === featureKey ? { ...item, is_enabled: newIsEnabled } : item
      )
    );
    setTogglingKeys((prev) => new Set(prev).add(featureKey));

    try {
      const response = await updateOrgFeature(
        featureKey,
        organizationId,
        newIsEnabled
      );

      if ([200, 201].includes(response.status)) {
        enqueueSnackbar(
          response?.data?.message?.success ||
            `Feature "${rowData.name}" ${newIsEnabled ? "enabled" : "disabled"} successfully`,
          { variant: "success" }
        );
      } else {
        throw new Error("Unexpected response");
      }
    } catch (error) {
      // Rollback
      setData((prev) =>
        prev.map((item) =>
          item.key === featureKey ? { ...item, is_enabled: currentIsEnabled } : item
        )
      );
      console.error("API Error:", error);
      enqueueSnackbar("Failed to update feature status", { variant: "error" });
    } finally {
      setTogglingKeys((prev) => {
        const next = new Set(prev);
        next.delete(featureKey);
        return next;
      });
    }
  };

  /* ─── Create / Edit modal ─── */

  const openCreateModal = () => {
    setEditKey(null);
    setFormData(EMPTY_FORM);
    setIsModalOpen(true);
  };

  const handleEdit = (rowData) => {
    setEditKey(rowData.key);
    setFormData({
      key: rowData.key || "",
      name: rowData.name || "",
      description: rowData.description || "",
      is_enabled_by_default: !!rowData.is_enabled_by_default,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditKey(null);
    setIsModalOpen(false);
  };

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onDefaultToggle = (e) => {
    setFormData((prev) => ({
      ...prev,
      is_enabled_by_default: e.target.checked,
    }));
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!formData.key.trim()) {
      enqueueSnackbar("Feature key is required", { variant: "error" });
      return;
    }
    if (!formData.name.trim()) {
      enqueueSnackbar("Feature name is required", { variant: "error" });
      return;
    }

    const payload = {
      key: formData.key.trim(),
      name: formData.name.trim(),
      description: formData.description.trim(),
      is_enabled_by_default: formData.is_enabled_by_default,
    };

    try {
      setIsLoader(true);
      const response = editKey
        ? await updateParentAppFeature(editKey, payload)
        : await createParentAppFeature(payload);

      if ([200, 201].includes(response.status)) {
        enqueueSnackbar(
          response?.data?.message?.success ||
            (editKey ? "Feature updated successfully" : "Feature saved successfully"),
          { variant: "success" }
        );
        closeModal();
        fetchFeatures();
      }
    } catch (error) {
      console.error("API Error:", error);
      enqueueSnackbar("Something went wrong", { variant: "error" });
    } finally {
      setIsLoader(false);
    }
  };

  /* ─── Render ─── */

  return (
    <div className="card">
      <CardHeader
        title="Parent App Features"
        icon={<FaMobileAlt />}
        button={
          permissions.includes("parent.app.feature.create") && (
            <Button type="submit" variant="primary" onClick={openCreateModal}>
              Add Feature
            </Button>
          )
        }
      />

      <div className="card-body">
        {/* Toolbar: search only */}
        <div className="paf-toolbar">
          <div className="paf-search-bar">
            <Input
              type="text"
              name="search"
              value={search}
              placeholder="Search by key or name…"
              onChangeHandler={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Create / Edit modal */}
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <h5>{editKey ? "EDIT FEATURE" : "ADD FEATURE"}</h5>
          <ParentAppFeatureForm
            submitHandler={submitHandler}
            changeHandler={changeHandler}
            formData={formData}
            onDefaultToggle={onDefaultToggle}
            isEditMode={!!editKey}
          />
        </Modal>

        {/* Table */}
        {isLoader ? (
          <Loader />
        ) : (
          <div className="row mt-3">
            <Table
              cols={tableConstants(
                permissions,
                handleToggle,
                togglingKeys,
                handleEdit
              )}
              data={filteredData}
              hoverable={true}
              striped={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentAppFeatureList;
